import React, { useState, useEffect } from "react";
import { Window } from "@progress/kendo-react-dialogs";
import { getUserCredential } from "../../../lib/cookies";
import projectSetting from "src/setting";
import { LoadingPanel } from "src/components/loadingPanel";
import "./FileViewerDialog.scss";

const FileViewerDialog = ({ visible, onClose, file }) => {
  const cookiesData = getUserCredential();
  const [fileUrl, setFileUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (visible && file) {
      loadFile();
    } else {
      // Cleanup URL when dialog closes
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
        setFileUrl(null);
      }
    }

    return () => {
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [visible, file]);

  const loadFile = async () => {
    if (!file?.document_path) return;

    setIsLoading(true);
    setError(null);

    try {
      const baseURL = projectSetting.baseURL + projectSetting.backend + projectSetting.version;

      const headers = {
        Connection: "keep-alive",
        "X-Access-Token": cookiesData?.secretToken || "",
        Accept: "application/json",
        "Content-Type": "application/json",
      };

      const body = {
        in_document_path: file.document_path,
        in_filter: null,
        in_sort_number: 1,
        in_sort_order: "ASC",
        in_content_type: file.document_type,
      };

      const response = await fetch(`${baseURL}/timesheet/download-document`, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error("Failed to load file");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setFileUrl(url);
    } catch (err) {
      console.error("Error loading file:", err);
      setError("Failed to load file. Please try downloading it instead.");
    } finally {
      setIsLoading(false);
    }
  };

  const getFileType = () => {
    if (!file?.document_type) return "unknown";

    const mimeType = file.document_type.toLowerCase();
    if (mimeType.startsWith("image/")) return "image";
    if (mimeType === "application/pdf") return "pdf";
    return "other";
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const ModalTitle = () => {
    return (
      <div className="file-viewer-header">
        <span className="file-viewer-title">{file?.original_name || "File Viewer"}</span>
        <span className="file-viewer-size">{formatFileSize(file?.document_size)}</span>
      </div>
    );
  };

  const renderFileContent = () => {
    if (isLoading) {
      return (
        <div className="file-viewer-loading">
          <LoadingPanel gridId="file-viewer-content" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="file-viewer-error">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
        </div>
      );
    }

    if (!fileUrl) {
      return (
        <div className="file-viewer-error">
          <i className="fas fa-exclamation-circle"></i>
          <span>No file to display</span>
        </div>
      );
    }

    const fileType = getFileType();

    switch (fileType) {
      case "image":
        return (
          <div className="file-viewer-image-container">
            <img src={fileUrl} alt={file?.original_name || "Document"} className="file-viewer-image" />
          </div>
        );
      case "pdf":
        return (
          <div className="file-viewer-pdf-container">
            <iframe src={fileUrl} className="file-viewer-pdf" title={file?.original_name || "PDF Document"} />
          </div>
        );
      default:
        return (
          <div className="file-viewer-other">
            <i className="fas fa-file"></i>
            <p>Preview not available for this file type.</p>
            <p>Please download the file to view it.</p>
          </div>
        );
    }
  };

  return (
    <Window
      visible={visible}
      onClose={onClose}
      title={<ModalTitle />}
      width={800}
      height={600}
      className="file-viewer-dialog"
      modal={true}
      closeIcon={true}
      backdrop={true}
    >
      <div className="file-viewer-content">{renderFileContent()}</div>
    </Window>
  );
};

export default FileViewerDialog;
